import { MAX_XP } from "./curve-expression.ts";
import { highestAtMost } from "./highest-at-most.ts";
import { remembering } from "./remembering.ts";
import {
  configuredRoleOf,
  curveCeiling,
  levelForXp,
  levelProgress,
  sameImportSettings,
  sameLevelReading,
  sameXpFormula,
  xpForLevel,
} from "./leveling.ts";
import type {
  Curve,
  Formula,
  LevelingConfig,
  LevelingState,
  PrestigeRule,
  RewardTrigger,
} from "./leveling.ts";

export type PrestigeFormula = Pick<Formula, "curve" | "maxLevel" | "prestige">;

export interface LevelPosition {
  readonly prestige: number;
  readonly level: number;
}

export interface LevelStanding extends LevelPosition {
  readonly xpIntoLevel: number;
  readonly xpForNext: number | null;
  readonly xpIntoPrestige: number;
  readonly xpForPrestige: number | null;
}

export const comparePositions = (left: LevelPosition, right: LevelPosition): number =>
  left.prestige - right.prestige || left.level - right.level;

interface Climbs {
  readonly curve: Curve;
  readonly cap: number;
  readonly rule: PrestigeRule;
  readonly starts: readonly number[];
  readonly flat: boolean;
  readonly flatCost: number;
}

const ceilingOf = (rule: PrestigeRule): number => (rule.growth === 0 ? 1 : rule.ceiling);

const multiplierOf = (rule: PrestigeRule, climb: number): number =>
  Math.min(ceilingOf(rule), (1 + rule.growth / 100) ** climb);

const scaledXp = (xp: number, multiplier: number): number => {
  if (multiplier === 1 || !Number.isFinite(xp)) return xp;
  let scaled = Math.ceil(xp * multiplier);
  if (scaled > MAX_XP) return scaled;
  while (scaled <= MAX_XP && scaled / multiplier < xp) scaled += 1;
  while (scaled > 0 && (scaled - 1) / multiplier >= xp) scaled -= 1;
  return scaled;
};

const remembered = remembering<Climbs>(32);

const buildClimbs = (curve: Curve, cap: number, rule: PrestigeRule): Climbs => {
  const first = xpForLevel(curve, cap);
  const flatCost = scaledXp(first, ceilingOf(rule));
  const starts = [0];
  let flat = true;
  for (let climb = 0; multiplierOf(rule, climb) < ceilingOf(rule); climb += 1) {
    const next = starts[climb]! + scaledXp(first, multiplierOf(rule, climb));
    if (next > MAX_XP) {
      flat = false;
      starts.push(next);
      break;
    }
    starts.push(next);
  }
  return { curve, cap, rule, starts, flat, flatCost };
};

const curveKeys = new WeakMap<Curve, string>();

const curveKey = (curve: Curve): string => {
  const known = curveKeys.get(curve);
  if (known !== undefined) return known;
  const key = JSON.stringify(curve);
  curveKeys.set(curve, key);
  return key;
};

const climbsOf = (formula: PrestigeFormula): Climbs | null => {
  const rule = formula.prestige;
  if (rule === undefined || rule === null || formula.maxLevel === null) return null;
  const cap = curveCeiling(formula.curve, formula.maxLevel);
  if (cap < 1) return null;
  const key = `${String(cap)}:${String(rule.growth)}:${String(rule.ceiling)}:${curveKey(formula.curve)}`;
  return remembered(key, () => buildClimbs(formula.curve, cap, rule));
};

export const reachesCeiling = (formula: {
  readonly curve: Curve;
  readonly maxLevel: number | null;
  readonly prestige?: PrestigeRule | null | undefined;
}): boolean => {
  const climbs = climbsOf(formula);
  return climbs === null || climbs.flat;
};

const climbStart = (climbs: Climbs, prestige: number): number => {
  const counted = climbs.starts.length - 1;
  if (prestige <= counted) return climbs.starts[prestige]!;
  return climbs.starts[counted]! + (prestige - counted) * climbs.flatCost;
};

const climbAt = (climbs: Climbs, xp: number): number => {
  const { starts } = climbs;
  const counted = starts.length - 1;
  if (xp >= starts[counted]! && climbs.flat) {
    const past = xp - starts[counted]!;
    let whole = Math.floor(past / climbs.flatCost);
    while (whole > 0 && whole * climbs.flatCost > past) whole -= 1;
    while ((whole + 1) * climbs.flatCost <= past) whole += 1;
    return counted + whole;
  }
  return highestAtMost(counted - 1, (climb) => starts[climb]!, xp);
};

const levelXpInClimb = (climbs: Climbs, prestige: number, level: number): number =>
  scaledXp(xpForLevel(climbs.curve, level), multiplierOf(climbs.rule, prestige));

const heldXp = (xp: number): number => (Number.isFinite(xp) && xp > 0 ? xp : 0);

const placeInClimbs = (
  climbs: Climbs,
  total: number,
): LevelPosition & { readonly into: number } => {
  const prestige = climbAt(climbs, total);
  const into = total - climbStart(climbs, prestige);
  const level = levelForXp(climbs.curve, into / multiplierOf(climbs.rule, prestige), climbs.cap);
  return { prestige, level, into };
};

export const standingForXp = (formula: PrestigeFormula, xp: number): LevelStanding => {
  const climbs = climbsOf(formula);
  const total = heldXp(xp);
  if (climbs === null) {
    const progress = levelProgress(formula.curve, total, formula.maxLevel);
    return { prestige: 0, ...progress, xpIntoPrestige: total, xpForPrestige: null };
  }
  const { prestige, level, into } = placeInClimbs(climbs, total);
  const floor = levelXpInClimb(climbs, prestige, level);
  const cost = levelXpInClimb(climbs, prestige, climbs.cap);
  return {
    prestige,
    level,
    xpIntoLevel: into - floor,
    xpForNext: levelXpInClimb(climbs, prestige, level + 1) - floor,
    xpIntoPrestige: into,
    xpForPrestige: cost,
  };
};

export const positionForXp = (formula: PrestigeFormula, xp: number): LevelPosition => {
  const climbs = climbsOf(formula);
  const total = heldXp(xp);
  if (climbs === null) {
    return { prestige: 0, level: levelForXp(formula.curve, total, formula.maxLevel) };
  }
  const { prestige, level } = placeInClimbs(climbs, total);
  return { prestige, level };
};

export const xpForPosition = (formula: PrestigeFormula, position: LevelPosition): number => {
  const climbs = climbsOf(formula);
  if (climbs === null) {
    return position.prestige > 0
      ? Number.POSITIVE_INFINITY
      : xpForLevel(formula.curve, position.level);
  }
  return (
    climbStart(climbs, position.prestige) +
    levelXpInClimb(climbs, position.prestige, position.level)
  );
};

export type KeptTrigger = Extract<RewardTrigger, { readonly kind: "level" | "prestige" }>;

export const isKeptTrigger = (trigger: RewardTrigger): trigger is KeptTrigger =>
  trigger.kind === "level" || trigger.kind === "prestige";

export const rewardPosition = (
  formula: PrestigeFormula,
  trigger: KeptTrigger,
): LevelPosition | null => {
  const climbs = climbsOf(formula);
  if (trigger.kind === "prestige") {
    return climbs === null ? null : { prestige: trigger.prestige, level: 0 };
  }
  const prestige = trigger.prestige === undefined ? 0 : trigger.prestige;
  const cap = curveCeiling(formula.curve, formula.maxLevel);
  if (trigger.level > cap) return null;
  if (climbs === null) return prestige === 0 ? { prestige: 0, level: trigger.level } : null;
  return trigger.level === cap
    ? { prestige: prestige + 1, level: 0 }
    : { prestige, level: trigger.level };
};

const levelRoleRewards = (config: LevelingConfig): string =>
  JSON.stringify({
    stacking: config.rewards.stacking,
    items: config.rewards.items
      .filter((item) => isKeptTrigger(item.trigger) && item.grant !== undefined)
      .map((item) => ({ id: item.id, trigger: item.trigger, role: configuredRoleOf(item.grant) }))
      .sort((left, right) => left.id.localeCompare(right.id)),
  });

export const needsRecompute = (before: LevelingState, after: LevelingState): boolean =>
  after.enabled &&
  (!before.enabled ||
    !sameLevelReading(before.config.formula, after.config.formula) ||
    !sameImportSettings(before.config.imports, after.config.imports) ||
    levelRoleRewards(before.config) !== levelRoleRewards(after.config));

export const changesEarning = (before: LevelingState, after: LevelingState): boolean =>
  before.enabled && after.enabled && !sameXpFormula(before.config.formula, after.config.formula);
